import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, classIcon, classTone, CLASS_EN } from './icons';

// TM RUSH client area: one page per order (/client#t=<token>), plus sign-in by email link (/client).
const API = 'https://n8n-hag0.srv1897462.hstgr.cloud/webhook';
const KEY_STORE = 'tmr_client_token';
const MAX_MB = 10;

// e-mails podem mandar direto para uma seção: /client#t=<token>&go=service-manifestacao
let GO_TARGET = '';
const readToken = () => {
  try {
    const g = window.location.hash.match(/go=([a-z0-9-]+)/);
    if (g) GO_TARGET = g[1];
    const m = window.location.hash.match(/t=([A-Za-z0-9_-]+)/);
    if (m) {
      try { localStorage.setItem(KEY_STORE, m[1]); } catch (e) { /* storage blocked */ }
      window.history.replaceState(null, '', window.location.pathname);
      return m[1];
    }
    return localStorage.getItem(KEY_STORE) || '';
  } catch (e) {
    return '';
  }
};

const fmtLong = (s) => {
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(s || '')) ? `${s}T12:00:00Z` : s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Sao_Paulo' });
};
const fmt = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Sao_Paulo' });
};

const MARK_TYPES = [
  ['word', 'Word mark', 'Text only, in standard letters'],
  ['mixed', 'Mixed mark', 'Text with a logo or stylized lettering'],
  ['figurative', 'Figurative mark', 'Logo only, no words'],
  ['3d', 'Three-dimensional', 'A distinctive shape'],
];

const card = 'bg-background-white rounded-2xl p-6 md:p-8 border border-gray-100/80 shadow-sm mb-6';
const inputCls = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';

const Shell = ({ children, onSignOut }) => (
  <main className="min-h-screen bg-background">
    <header className="bg-text text-white">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-3">
        <a href="/" className="flex items-center gap-3 min-w-0">
          <img src="/images/tm-rushlogo.svg" alt="TM RUSH" className="h-6 brightness-0 invert shrink-0" />
          <span className="text-sm font-semibold bg-white/10 border border-white/20 rounded-full px-3 py-1 whitespace-nowrap">Client area</span>
        </a>
        <div className="flex items-center gap-4 text-xs text-gray-300">
          <a href="mailto:contact@tmrush.com" className="hover:text-white hidden sm:inline">contact@tmrush.com</a>
          {onSignOut && <button type="button" onClick={onSignOut} className="hover:text-white underline-offset-2 hover:underline">Sign out</button>}
        </div>
      </div>
    </header>
    <div className="container mx-auto px-4 py-8 max-w-4xl">{children}</div>
  </main>
);

const Field = ({ label, hint, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-sm font-semibold text-text mb-1">{label}</span>
    {children}
    {hint && <span className="block text-xs text-text/55 mt-1">{hint}</span>}
  </label>
);

const readReset = () => {
  try {
    const m = window.location.hash.match(/r=([A-Za-z0-9_-]+)/);
    if (m) {
      window.history.replaceState(null, '', window.location.pathname);
      return m[1];
    }
  } catch (e) { /* ignore */ }
  return '';
};

const postForm = async (path, body) => {
  const r = await fetch(`${API}/${path}`, { method: 'POST', body: new URLSearchParams(body) });
  const j = await r.json().catch(() => ({}));
  if (!j.ok) throw new Error(j.error || 'Something went wrong. Please try again.');
  return j;
};

const LoginView = ({ reset, onToken }) => {
  const [mode, setMode] = useState(reset ? 'set' : 'link');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = (fn) => async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try { await fn(); } catch (ex) { setErr(ex.message || 'Something went wrong. Please try again.'); } finally { setBusy(false); }
  };
  const signIn = async (em, p) => {
    const j = await postForm('tmr-auth-login', { email: em, password: p, kind: 'client' });
    if (j.orders.length === 1) onToken(j.orders[0].token);
    else { setOrders(j.orders); setMode('choose'); }
  };
  const checkEmail = () => { if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) throw new Error('Please enter a valid email address.'); };
  const onLink = run(async () => { checkEmail(); await postForm('tmr-client-login', { email }); setMode('sent'); });
  const onPassword = run(() => { checkEmail(); if (!pw) throw new Error('Please enter your password.'); return signIn(email.trim(), pw); });
  const onForgot = run(async () => { await postForm('tmr-auth-reset', { email: email.trim(), kind: 'client' }); setMode('reset_sent'); });
  const onSet = run(async () => {
    if (pw.length < 8) throw new Error('Please use at least 8 characters.');
    if (pw !== pw2) throw new Error('The two passwords do not match.');
    const j = await postForm('tmr-auth-set', { token: reset, password: pw, lang: 'en' });
    await signIn(j.email, pw);
  });
  const tab = (id, label) => <button type="button" onClick={() => { setErr(''); setMode(id); }} className={`flex-1 text-sm font-semibold rounded-lg px-3 py-2 ${mode === id ? 'bg-background-white text-text shadow-sm' : 'text-text/60'}`}>{label}</button>;
  const errBox = err && <div className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2 mt-3">{err}</div>;
  const btn = 'mt-5 w-full bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary-hover font-semibold shadow-md disabled:opacity-60';
  return (
    <Shell>
      <div className="max-w-md mx-auto bg-background-white rounded-2xl p-8 shadow-sm border border-gray-100/80 mt-6">
        <h1 className="text-2xl font-bold text-text mb-4">{mode === 'set' ? 'Choose your password' : 'Client login'}</h1>
        {['link', 'password'].includes(mode) && <div className="flex gap-1 bg-background-auxiliary rounded-xl p-1 mb-5">{tab('link', 'Email me a link')}{tab('password', 'Password')}</div>}
        {mode === 'link' && (
          <form onSubmit={onLink} noValidate>
            <p className="text-text/70 text-sm leading-relaxed mb-5">Enter the email address you used for your order. We will email you a link to your client area.</p>
            <Field label="Email address">
              <input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@company.com" />
            </Field>
            {errBox}
            <button type="submit" disabled={busy} className={btn}>{busy ? 'Sending…' : 'Email me my sign-in link'}</button>
          </form>
        )}
        {mode === 'password' && (
          <form onSubmit={onPassword} noValidate>
            <Field label="Email address">
              <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@company.com" />
            </Field>
            <Field label="Password" className="mt-4">
              <input type="password" required autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} />
            </Field>
            {errBox}
            <button type="submit" disabled={busy || !pw} className={btn}>{busy ? 'Signing in…' : 'Sign in'}</button>
            <button type="button" disabled={busy} onClick={(e) => { if (!email.trim()) { setErr('Enter your email address first.'); return; } onForgot(e); }} className="mt-4 w-full text-sm font-semibold text-primary-dark hover:underline">Set or reset my password</button>
          </form>
        )}
        {mode === 'sent' && (
          <div>
            <p className="text-text/80 text-sm leading-relaxed mb-4">If there is an order under <b>{email}</b>, we have just emailed you a sign-in link. It usually arrives within a minute. Please also check your spam folder.</p>
            <button type="button" onClick={() => setMode('link')} className="text-sm font-semibold text-primary-dark hover:underline">Use another email</button>
          </div>
        )}
        {mode === 'reset_sent' && (
          <div>
            <p className="text-text/80 text-sm leading-relaxed mb-4">If there is an order under <b>{email}</b>, we have just emailed you a link to set your password. It is valid for 1 hour.</p>
            <button type="button" onClick={() => setMode('password')} className="text-sm font-semibold text-primary-dark hover:underline">Back to sign in</button>
          </div>
        )}
        {mode === 'set' && (
          <form onSubmit={onSet} noValidate>
            <p className="text-text/70 text-sm leading-relaxed mb-5">At least 8 characters. Please use a password you do not use anywhere else.</p>
            <Field label="New password">
              <input type="password" required autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Repeat the password" className="mt-4">
              <input type="password" required autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputCls} />
            </Field>
            {errBox}
            <button type="submit" disabled={busy || !pw || !pw2} className={btn}>{busy ? 'Saving…' : 'Save and sign in'}</button>
          </form>
        )}
        {mode === 'choose' && (
          <div>
            <p className="text-text/70 text-sm mb-4">Choose an order:</p>
            <div className="space-y-2">
              {orders.map((o) => (
                <button key={o.token} type="button" onClick={() => onToken(o.token)} className="w-full text-left rounded-xl border border-gray-100 px-4 py-3 hover:border-primary/40">
                  <div className="font-semibold text-text">{o.mark || o.ref}</div>
                  <div className="text-xs text-text/60">Order {o.ref}{o.paid_at ? ` · ${fmt(o.paid_at)}` : ''}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-text/50 mt-6">Not a client yet? <a href="/order" className="text-primary-dark font-semibold hover:underline">Start an application</a></p>
      </div>
    </Shell>
  );
};

const initForm = (v) => {
  const d = v.details || {};
  const goods = {};
  (v.class_list.length ? v.class_list : ['all']).forEach((c) => { goods[c] = (d.goods && d.goods[c]) || ''; });
  return {
    owner_type: d.owner_type || v.owner_type_hint || 'company', owner_name: d.owner_name || v.owner || '', owner_country: d.owner_country || '', owner_state: d.owner_state || '',
    owner_reg_no: d.owner_reg_no || '', owner_id_no: d.owner_id_no || '',
    addr_street: d.addr_street || '', addr_city: d.addr_city || '', addr_region: d.addr_region || '', addr_postal: d.addr_postal || '', addr_country: d.addr_country || '',
    signatory_name: d.signatory_name || '', signatory_title: d.signatory_title || '',
    mark_type: d.mark_type || '', mark_text: d.mark_text != null && d.updated_at ? d.mark_text : (v.marks.join(', ') || v.trademark || ''), mark_translation: d.mark_translation || '',
    goods, goods_help: !!d.goods_help, business_desc: d.business_desc || '',
    priority: !!d.priority, priority_country: d.priority_country || '', priority_number: d.priority_number || '', priority_date: d.priority_date || '',
    contact_name: d.contact_name || v.customer_name || '', contact_email: d.contact_email || v.customer_email || '', client_ref: d.client_ref || '', notes: d.notes || '',
  };
};

const Uploader = ({ token, kind, label, accept, onDone, compact, extra }) => {
  const ref = useRef(null);
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const send = async () => {
    const f = ref.current && ref.current.files && ref.current.files[0];
    setErr(''); setMsg('');
    if (!f) { setErr('Please choose a file first.'); return; }
    if (f.size > MAX_MB * 1024 * 1024) { setErr(`This file is too large. The limit is ${MAX_MB} MB.`); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('t', token); fd.append('kind', kind); Object.entries(extra || {}).forEach(([k, v]) => fd.append(k, v)); fd.append('file', f, f.name);
      const r = await fetch(`${API}/tmr-client-upload`, { method: 'POST', body: fd });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Upload failed.');
      setMsg(`Received: ${f.name}`);
      ref.current.value = '';
      setPicked('');
      onDone(j.view);
    } catch (ex) {
      setErr(ex.message || 'Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className={`flex flex-col sm:flex-row gap-3 ${compact ? '' : 'sm:items-center'}`}>
        <label className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer">
          <input ref={ref} type="file" accept={accept} className="sr-only peer" onChange={(e) => { setErr(''); setMsg(''); setPicked((e.target.files && e.target.files[0] && e.target.files[0].name) || ''); }} />
          <span className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-background-auxiliary px-4 py-2 text-sm font-semibold text-text hover:bg-gray-200 peer-focus-visible:ring-2 peer-focus-visible:ring-primary min-h-[40px]"><Icon name="folder" size={16} />Choose file</span>
          <span className={`text-sm truncate ${picked ? 'text-text' : 'text-text/55'}`}>{picked || 'No file chosen'}</span>
        </label>
        <button type="button" disabled={busy} onClick={send} className="shrink-0 bg-text text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 disabled:opacity-50">{busy ? 'Uploading…' : label}</button>
      </div>
      {msg && <div className="text-sm text-primary-dark mt-2">{msg}</div>}
      {err && <div className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2 mt-2">{err}</div>}
    </div>
  );
};

// serviços que o cliente contrata aqui: cartão (Stripe) ou transferência (invoice); a equipe começa quando estiver pago
const daysTo = (iso) => { const t = Date.parse(`${String(iso).slice(0, 10)}T12:00:00Z`); return Number.isNaN(t) ? null : Math.ceil((t - Date.now()) / 864e5); };
const ServiceOffer = ({ x, token, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const days = x.deadline ? daysTo(x.deadline) : null;
  const requestInvoice = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch(`${API}/tmr-client-service`, { method: 'POST', body: new URLSearchParams({ t: token, service: x.key, method: 'transfer', process: x.process || '' }) });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'We could not request the invoice. Please try again.');
      setMsg(`Invoice ${j.invoice} requested. We email it to you within one business day.`);
      onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const tone = days != null && days <= 15 ? 'bg-red-50 border-red-200' : 'bg-amber-50/60 border-amber-200';
  return (
    <div id={`service-${x.key}`} className={`rounded-2xl border p-4 md:p-5 scroll-mt-24 ${x.hired ? 'bg-background-white border-gray-100' : tone}`}>
      <div className="flex flex-wrap items-start gap-4">
        {x.deadline && (
          <div className="w-24 shrink-0 rounded-xl bg-background-white border border-gray-200 overflow-hidden text-center">
            <div className={`text-[10px] font-bold uppercase tracking-wider text-white py-1 ${days != null && days <= 15 ? 'bg-red-700' : 'bg-amber-700'}`}>{x.grace ? 'Last day' : 'Deadline'}</div>
            <div className="py-2"><div className="text-3xl font-extrabold text-text leading-none">{fmtLong(x.deadline).split(' ')[0]}</div><div className="text-xs font-bold text-text mt-1">{fmtLong(x.deadline).split(' ').slice(1).join(' ')}</div>{days != null && <div className="text-[11px] text-text/55 mt-0.5">{days >= 0 ? `${days} days left` : 'passed'}</div>}</div>
          </div>
        )}
        <div className="min-w-[220px] flex-1">
          <h3 className="font-bold text-text text-base">{x.title}</h3>
          <div className="text-xs text-text/60 mb-2">{[x.process && `Application no. ${x.process}`, x.cls && `Class ${x.cls}`].filter(Boolean).join(' · ')}</div>
          <p className="text-sm text-text/75 mb-3">{x.why}</p>
          {x.hired ? (
            <div className="space-y-3">
              <span className={`inline-flex text-xs font-semibold rounded-full px-3 py-1 ${x.status === 'waiting_payment' ? 'bg-amber-50 text-amber-800' : 'bg-primary/15 text-primary-dark'}`}>{x.status === 'waiting_payment' ? `Invoice ${x.invoice} · waiting for payment` : x.status === 'filed' ? 'Filed with INPI' : 'Paid · in progress'}</span>
              {x.status === 'waiting_payment' && (
                <div>
                  <div className="text-sm text-text/75 mb-2">When you have paid, upload the bank receipt here (SWIFT / MT103 copy): we start as soon as we have it.</div>
                  <Uploader token={token} kind="payment_proof" extra={{ service: x.key }} label="Upload the transfer receipt" accept="application/pdf,image/*" onDone={onChanged} />
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="inline-flex items-center rounded-lg bg-background-white border border-gray-200 px-3 py-1.5 text-sm font-bold text-text mb-3">{x.price_label} · official fee included</div>
              <div className="flex flex-wrap gap-2">
                {x.card_url && <a href={x.card_url} className="inline-flex items-center gap-2 bg-text text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 min-h-[44px]"><Icon name="card" size={17} />Pay by card</a>}
                <button type="button" disabled={busy} onClick={requestInvoice} className="inline-flex items-center gap-2 bg-background-white border border-gray-300 text-text px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-50 min-h-[44px] disabled:opacity-50"><Icon name="banknote" size={17} />{busy ? 'Requesting…' : 'Pay by bank transfer'}</button>
                <a href="#messages" className="inline-flex items-center gap-2 text-primary-dark px-3 py-2.5 rounded-xl font-semibold text-sm hover:underline min-h-[44px]"><Icon name="message" size={17} />Ask us a question</a>
              </div>
              <div className="text-xs text-text/55 mt-2">We start as soon as it is paid. {x.card_url ? 'Card payment is instant. ' : ''}If you pay by bank transfer, please allow 10 days before the deadline.</div>
            </>
          )}
          {msg && <div className="text-sm text-primary-dark mt-2">{msg}</div>}
          {err && <div className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2 mt-2">{err}</div>}
        </div>
      </div>
    </div>
  );
};

const DetailsForm = ({ view, token, onSaved }) => {
  const [f, setF] = useState(() => initForm(view));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const setGood = (c) => (e) => setF({ ...f, goods: { ...f.goods, [c]: e.target.value } });
  const ck = view.checklist;
  const company = f.owner_type === 'company';
  const individual = f.owner_type === 'individual';
  const needsLogo = ['figurative', 'mixed', '3d'].includes(f.mark_type);
  const logos = view.files.filter((x) => x.kind === 'logo');

  const save = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await fetch(`${API}/tmr-client-save`, { method: 'POST', body: new URLSearchParams({ t: token, details: JSON.stringify(f) }) });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Could not save.');
      onSaved(j.view);
      setMsg(j.view.checklist.details ? 'Saved. Your details are complete and your Power of Attorney now carries them.' : 'Saved. Some items are still missing: see the checklist above.');
    } catch (ex) {
      setErr(ex.message || 'Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const Section = ({ n, title, ok, children }) => (
    <div className="border-t border-gray-100 pt-6 mt-6 first:border-0 first:pt-0 first:mt-0">
      <div className="flex items-center gap-3 mb-4">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${ok ? 'bg-primary text-white' : 'bg-background-auxiliary text-text'}`}>{ok ? '✓' : n}</span>
        <h3 className="text-base font-bold text-text">{title}</h3>
      </div>
      {children}
    </div>
  );

  return (
    <section id="details" className={card}>
      <h2 className="text-lg font-bold text-text mb-1">Order details</h2>
      <p className="text-sm text-text/60 mb-6">We use these details to file and to prepare your Power of Attorney. You can save and come back later.</p>

      {Section({ n: 1, title: 'Trademark owner', ok: ck.owner, children: (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            {[['company', 'A company'], ['individual', 'An individual']].map(([k, l]) => (
              <label key={k} className={`cursor-pointer rounded-xl border px-4 py-2.5 text-sm font-semibold ${f.owner_type === k ? 'border-primary bg-primary/10 text-text' : 'border-gray-200 text-text/70'}`}>
                <input type="radio" name="owner_type" value={k} checked={f.owner_type === k} onChange={set('owner_type')} className="sr-only" />{l}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={individual ? 'Full name' : 'Legal name of the company'} className="md:col-span-2"><input value={f.owner_name} onChange={set('owner_name')} className={inputCls} /></Field>
            <Field label={individual ? 'Nationality (country)' : 'Country of incorporation'}><input value={f.owner_country} onChange={set('owner_country')} className={inputCls} placeholder={individual ? 'e.g. Italy' : 'e.g. United States'} /></Field>
            {company ? (
              <Field label="State or province of incorporation" hint="Optional, e.g. Delaware"><input value={f.owner_state} onChange={set('owner_state')} className={inputCls} /></Field>
            ) : (
              <Field label="Passport or ID number" hint="Optional"><input value={f.owner_id_no} onChange={set('owner_id_no')} className={inputCls} /></Field>
            )}
            {company && <Field label="Company registration number" hint="Optional"><input value={f.owner_reg_no} onChange={set('owner_reg_no')} className={inputCls} /></Field>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Street address" className="md:col-span-2" hint={view.billing_address ? `Billing address on file: ${view.billing_address}` : ''}><input value={f.addr_street} onChange={set('addr_street')} className={inputCls} /></Field>
            <Field label="City"><input value={f.addr_city} onChange={set('addr_city')} className={inputCls} /></Field>
            <Field label="State / province"><input value={f.addr_region} onChange={set('addr_region')} className={inputCls} /></Field>
            <Field label="Postal code"><input value={f.addr_postal} onChange={set('addr_postal')} className={inputCls} /></Field>
            <Field label="Country"><input value={f.addr_country} onChange={set('addr_country')} className={inputCls} /></Field>
          </div>
          {company && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Who will sign (optional)" hint="Name of the officer or authorized person"><input value={f.signatory_name} onChange={set('signatory_name')} className={inputCls} /></Field>
              <Field label="Their title (optional)" hint="e.g. CEO, Director, General Counsel"><input value={f.signatory_title} onChange={set('signatory_title')} className={inputCls} /></Field>
            </div>
          )}
        </div>
      ) })}

      {Section({ n: 2, title: 'The trademark', ok: ck.mark, children: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {MARK_TYPES.map(([k, l, s]) => (
              <label key={k} className={`cursor-pointer rounded-xl border px-4 py-3 ${f.mark_type === k ? 'border-primary bg-primary/10' : 'border-gray-200'}`}>
                <input type="radio" name="mark_type" value={k} checked={f.mark_type === k} onChange={set('mark_type')} className="sr-only" />
                <span className="block text-sm font-semibold text-text">{l}</span>
                <span className="block text-xs text-text/55">{s}</span>
              </label>
            ))}
          </div>
          {f.mark_type !== 'figurative' && (
            <Field label="Text of the trademark" hint="Exactly as it should be registered"><input value={f.mark_text} onChange={set('mark_text')} className={inputCls} /></Field>
          )}
          <Field label="Meaning or translation" hint="Optional. If the mark has words in a language other than Portuguese, tell us what they mean (or that they are invented)."><input value={f.mark_translation} onChange={set('mark_translation')} className={inputCls} /></Field>
          {needsLogo && (
            <div className="rounded-xl bg-background-auxiliary p-4">
              <div className="text-sm font-semibold text-text mb-1">Logo file</div>
              <p className="text-xs text-text/60 mb-3">PNG, JPG or PDF, up to {MAX_MB} MB. INPI files the logo in the colors you send.</p>
              {logos.length > 0 && <div className="text-sm text-primary-dark mb-3">✓ {logos.map((x) => x.name).join(', ')}</div>}
              <Uploader token={token} kind="logo" label={logos.length ? 'Replace logo' : 'Upload logo'} accept="image/*,application/pdf" onDone={onSaved} compact />
            </div>
          )}
        </div>
      ) })}

      {Section({ n: 3, title: 'Goods and services', ok: ck.goods, children: (
        <div className="space-y-4">
          <p className="text-sm text-text/65">List what you sell or offer under the mark, in English. We adapt the wording to INPI's standards before filing and confirm it with you.</p>
          {!f.goods_help && Object.keys(f.goods).map((c) => (
            <Field key={c} label={c === 'all' ? 'Goods and services' : `Class ${c}`}>
              <textarea rows={3} value={f.goods[c]} onChange={setGood(c)} className={inputCls} placeholder={c === 'all' ? 'e.g. mobile apps for tracking video game releases' : ''} />
            </Field>
          ))}
          <label className="flex items-start gap-3 text-sm text-text cursor-pointer">
            <input type="checkbox" checked={f.goods_help} onChange={set('goods_help')} className="mt-1" />
            <span>I would rather describe my business and have TM RUSH draft the list for my approval.</span>
          </label>
          {f.goods_help && (
            <Field label="Describe your business and your products or services"><textarea rows={4} value={f.business_desc} onChange={set('business_desc')} className={inputCls} /></Field>
          )}
          <p className="text-xs text-text/50">Need another class? Email contact@tmrush.com and we will add it to your order.</p>
        </div>
      ) })}

      {Section({ n: 4, title: 'Priority and contact (optional)', ok: false, children: (
        <div className="space-y-4">
          <label className="flex items-start gap-3 text-sm text-text cursor-pointer">
            <input type="checkbox" checked={f.priority} onChange={set('priority')} className="mt-1" />
            <span>Claim priority from an application filed in another country in the last 6 months.</span>
          </label>
          {f.priority && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Country"><input value={f.priority_country} onChange={set('priority_country')} className={inputCls} /></Field>
              <Field label="Application number"><input value={f.priority_number} onChange={set('priority_number')} className={inputCls} /></Field>
              <Field label="Filing date (YYYY-MM-DD)"><input type="text" inputMode="numeric" maxLength={10} placeholder="YYYY-MM-DD" value={f.priority_date} onChange={set('priority_date')} className={inputCls} /></Field>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Contact name"><input value={f.contact_name} onChange={set('contact_name')} className={inputCls} /></Field>
            <Field label="Contact email"><input type="email" value={f.contact_email} onChange={set('contact_email')} className={inputCls} /></Field>
            <Field label="Your reference" hint="Optional, e.g. your docket number"><input value={f.client_ref} onChange={set('client_ref')} className={inputCls} /></Field>
          </div>
          <Field label="Anything else we should know?"><textarea rows={3} value={f.notes} onChange={set('notes')} className={inputCls} /></Field>
        </div>
      ) })}

      <div className="border-t border-gray-100 pt-6 mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <button type="button" disabled={busy} onClick={save} className="bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary-hover font-semibold shadow-md disabled:opacity-60">{busy ? 'Saving…' : 'Save details'}</button>
        {msg && <span className="text-sm text-primary-dark">{msg}</span>}
        {err && <span className="text-sm text-red-700">{err}</span>}
      </div>
    </section>
  );
};

const Messages = ({ view, token, onSent }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setNote('');
    try {
      const r = await fetch(`${API}/tmr-client-message`, { method: 'POST', body: new URLSearchParams({ t: token, message: text.trim() }) });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Your message was not sent. Please try again.');
      setText('');
      setNote('Message sent. We will reply by email and here.');
      onSent(j.view);
    } catch (e) {
      setNote(e.message || 'Your message was not sent. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const list = view.messages || [];
  return (
    <section id="messages" className="bg-background-white rounded-3xl p-5 md:p-6 border border-gray-100/80 shadow-sm">
      <h2 className="text-lg font-bold text-text mb-1 flex items-center gap-2.5"><Icon name="message" size={22} className="text-primary-dark" />Questions?</h2>
      <p className="text-sm text-text/65 mb-5">Write to our team. We reply by email, and the conversation stays on this page.</p>
      {list.length > 0 && (
        <ul className="space-y-4 mb-5">
          {list.map((m, i) => (
            <li key={i} className="space-y-2">
              <div className="bg-background-auxiliary rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-text whitespace-pre-line">
                <div className="text-[11px] text-text/50 mb-1">You{m.via === 'email' ? ' (by email)' : ''} · {fmt(m.at)}</div>
                {m.text}
              </div>
              {m.reply ? (
                <div className="bg-primary/10 rounded-2xl rounded-tr-sm px-4 py-3 text-sm text-text whitespace-pre-line ml-6">
                  <div className="text-[11px] text-primary-dark mb-1">TM RUSH · {fmt(m.reply_at)}</div>
                  {m.reply}
                </div>
              ) : <div className="text-xs text-text/50 ml-6">We received your message and will reply soon.</div>}
            </li>
          ))}
        </ul>
      )}
      <label className="block text-sm font-semibold text-text mb-1" htmlFor="msg">Your message</label>
      <textarea id="msg" rows={4} maxLength={3000} value={text} onChange={(e) => setText(e.target.value)} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="Write your question here" />
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <button type="button" disabled={busy || !text.trim()} onClick={send} className="inline-flex items-center gap-2 bg-text text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 disabled:opacity-50 min-h-[44px]"><Icon name="send" size={16} />{busy ? 'Sending…' : 'Send message'}</button>
        {note && <span className="text-sm text-text/70">{note}</span>}
      </div>
      <dl className="text-sm space-y-2 border-t border-gray-100 pt-4 mt-5">
        <div className="flex justify-between gap-3"><dt className="text-text/60">Order</dt><dd className="font-semibold text-text">{view.ref}</dd></div>
        {view.payment && view.payment.method === 'transfer' && <div className="flex justify-between gap-3"><dt className="text-text/60">Invoice</dt><dd className="font-semibold text-text">{[view.payment.invoice, view.payment.total].filter(Boolean).join(' · ')}</dd></div>}
        {view.paid_at && <div className="flex justify-between gap-3"><dt className="text-text/60">{view.payment && view.payment.method === 'transfer' ? 'Transfer receipt' : 'Paid'}</dt><dd className="font-semibold text-text">{view.payment && view.payment.method === 'transfer' ? fmt(view.paid_at) : `${view.amount} · ${fmt(view.paid_at)}`}</dd></div>}
        <div className="flex justify-between gap-3"><dt className="text-text/60">Updates go to</dt><dd className="font-semibold text-text truncate">{view.customer_email}</dd></div>
      </dl>
    </section>
  );
};

// ---------- visual pieces of the client area (journey, class cards, watch, documents) ----------
const SHORT = (s) => {
  if (!s) return '';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(s)) ? `${s}T12:00:00Z` : s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'America/Sao_Paulo' });
};
const dmyIso = (s) => { const m = String(s || '').match(/^(\d{2})\/(\d{2})\/(\d{4})/); return m ? `${m[3]}-${m[2]}-${m[1]}` : String(s || '').slice(0, 10); };
const todayIso = () => new Date(Date.now() - 3 * 36e5).toISOString().slice(0, 10);
const daysBetween = (a, b) => Math.round((Date.parse(`${String(b).slice(0, 10)}T12:00:00Z`) - Date.parse(`${String(a).slice(0, 10)}T12:00:00Z`)) / 864e5);
const RANK = { filed: 0, formal_requirement: 0, opposition_window: 1, opposition_filed: 1, exam: 2, office_action: 2, suspended: 2, appeal_notified: 2, refused: 2, refused_final: 2, allowed: 3, registered: 4 };
const STAGE_ICON = { filed: 'clock', formal_requirement: 'alert', opposition_window: 'shield', opposition_filed: 'alert', exam: 'search', office_action: 'alert', suspended: 'clock', allowed: 'check', registered: 'award', refused: 'alert', refused_final: 'alert', appeal_notified: 'scale' };
const NEXT_EN = ['Publication', 'Examination', 'INPI decision', 'Registration', 'Use check, 5 years'];
const isWatchOnly = (d) => d.kind === 'Brazil Trademark Watch';

// pedido por transferência: o comprovante (SWIFT) libera o processo
const payPending = (d) => !!(d.payment && d.payment.pending);
// cada quadrinho mostra o status real: feito (verde, com data), aguardando (amarelo), alerta (vermelho), ainda não (cinza)
const STAGE_TONE = { filed: 'wait', formal_requirement: 'wait', opposition_window: 'wait', opposition_filed: 'alert', exam: 'wait', office_action: 'alert', suspended: 'wait', appeal_notified: 'wait', refused: 'alert', refused_final: 'alert', allowed: 'done', registered: 'done' };
const journeyOf = (d) => {
  const st = Object.fromEntries((d.steps || []).map((s) => [s.key, s]));
  const ck = d.checklist || {};
  const items = (d.watch && d.watch.items) || [];
  const procs = d.processes || [];
  const ranks = items.map((i) => (RANK[i.stage] != null ? RANK[i.stage] : 0));
  const filed = !!(ck.filed || procs.length || items.length || (st.filed && st.filed.done));
  const minRank = ranks.length ? Math.min(...ranks) : (procs.some((p) => p.rpi) ? 1 : filed ? 0 : -1);
  const pubs = [...new Set(items.filter((i) => i.stage === 'opposition_window' && i.stage_since).map((i) => SHORT(i.stage_since)))];
  if (!pubs.length) items.forEach((i) => { if (i.pub_rpi && !pubs.includes(`Gazette ${i.pub_rpi}`)) pubs.push(`Gazette ${i.pub_rpi}`); });
  const grant = items.map((i) => i.grant_date).filter(Boolean).sort()[0] || '';
  const has = (...k) => items.some((i) => k.includes(i.stage));
  const oppEnd = items.flatMap((i) => i.deadlines || []).filter((x) => x.key === 'opposition' && x.days >= 0).map((x) => x.date).sort()[0] || '';
  const paid = !payPending(d) && !!(d.paid_at || (st.paid && st.paid.done));
  const det = !!ck.details || filed;
  const poa = !!ck.poa || filed;
  const S = (key, icon, label, state, status) => ({ key, icon, label, state, status });
  let steps = [
    paid ? S('paid', 'card', 'Paid', 'done', SHORT(d.paid_at) || 'Done') : S('paid', 'card', 'Payment', 'wait', 'Waiting for payment'),
    det ? S('details', 'form', 'Your details', 'done', 'Done') : S('details', 'form', 'Your details', 'wait', 'Waiting for you'),
    poa ? S('poa', 'docSign', 'Power of Attorney', 'done', SHORT(st.poa_received && st.poa_received.at) || 'Received') : S('poa', 'docSign', 'Power of Attorney', 'wait', 'Waiting for you'),
    filed ? S('filed', 'landmark', isWatchOnly(d) ? 'On file at INPI' : 'Filed with INPI', 'done', SHORT(st.filed && st.filed.at) || 'Done')
      : S('filed', 'landmark', 'Filing', paid && det && poa ? 'wait' : 'todo', paid && det && poa ? 'In progress' : ''),
    minRank >= 1 ? S('published', 'newspaper', 'Published', 'done', pubs.join(' · ') || 'Done')
      : S('published', 'newspaper', 'Publication', filed ? 'wait' : 'todo', filed ? (has('formal_requirement') ? 'Formal requirement' : 'Waiting for INPI') : ''),
    minRank >= 2 ? S('opposition', 'shield', 'Opposition period', 'done', 'Closed')
      : minRank === 1 ? S('opposition', 'shield', 'Opposition period', has('opposition_filed') ? 'alert' : 'wait', has('opposition_filed') ? 'Opposition filed' : oppEnd ? `Open until ${SHORT(oppEnd)}` : 'Open')
        : S('opposition', 'shield', 'Opposition period', 'todo', ''),
    minRank >= 3 ? S('exam', 'search', 'Examination', 'done', 'Approved')
      : minRank === 2 ? (has('office_action') ? S('exam', 'search', 'Examination', 'alert', 'Office action') : has('refused', 'refused_final') ? S('exam', 'search', 'Examination', 'alert', 'Refused')
        : S('exam', 'search', 'Examination', 'wait', has('suspended') ? 'Suspended' : has('appeal_notified') ? 'Appeal' : 'In progress'))
        : S('exam', 'search', 'Examination', 'todo', ''),
    minRank >= 4 ? S('registered', 'award', 'Registered', 'done', SHORT(grant) || 'Done') : S('registered', 'award', 'Registration', minRank === 3 ? 'wait' : 'todo', minRank === 3 ? 'Coming soon' : ''),
  ];
  if (isWatchOnly(d)) steps = steps.filter((s) => !['details', 'poa'].includes(s.key));
  const cur = steps.findIndex((s) => s.state !== 'done');
  return { steps: steps.map((s, i) => ({ ...s, current: i === cur })), cur, done: steps.filter((s) => s.state === 'done').length, filed, minRank };
};

const STEP_LOOK = {
  done: { card: 'bg-primary/10', dot: 'bg-primary text-text', label: 'text-text', status: 'text-primary-dark', ring: '' },
  wait: { card: 'bg-amber-50', dot: 'bg-amber-400 text-text', label: 'text-text', status: 'text-amber-800', ring: 'ring-4 ring-amber-300/70' },
  alert: { card: 'bg-red-50', dot: 'bg-red-600 text-white', label: 'text-text', status: 'text-red-700', ring: 'ring-4 ring-red-300/70' },
  todo: { card: 'bg-background-auxiliary/70', dot: 'bg-white border-2 border-gray-200 text-text/45', label: 'text-text/70', status: 'text-text/50', ring: '' },
};
const Journey = ({ j, banner, onGo }) => {
  const pct = Math.round((j.done / j.steps.length) * 100);
  const curStep = j.steps[j.cur];
  return (
    <section className="bg-background-white rounded-3xl border border-gray-100/80 shadow-sm p-5 md:p-7 mb-6" aria-labelledby="journey-title">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h2 id="journey-title" className="text-xl md:text-2xl font-bold text-text">Your trademark journey</h2>
          <p className="text-sm text-text/60 mt-1">{curStep ? `Step ${j.cur + 1} of ${j.steps.length} · ${curStep.label}${curStep.status ? ` · ${curStep.status}` : ''}` : 'All steps complete'}</p>
        </div>
        <div className="w-full sm:w-72">
          <div className="h-2.5 rounded-full bg-background-auxiliary overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} /></div>
          <div className="text-xs text-text/55 mt-1.5 sm:text-right">{pct}% of the way to registration</div>
        </div>
      </div>
      <ol className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5" aria-label="Journey steps">
        {j.steps.map((s) => {
          const L = STEP_LOOK[s.state] || STEP_LOOK.todo;
          return (
            <li key={s.key} className={`rounded-2xl px-2 py-4 flex flex-col items-center text-center gap-2 ${L.card} ${s.current ? L.ring : ''}`} aria-current={s.current ? 'step' : undefined}>
              <span className={`w-12 h-12 rounded-full flex items-center justify-center ${L.dot}`}>
                <Icon name={s.state === 'alert' ? 'alert' : s.icon} size={22} />
              </span>
              <span className={`text-[13px] font-bold leading-tight ${L.label}`}>{s.label}</span>
              <span className={`text-[11px] font-semibold leading-tight ${L.status}`}>{s.status}</span>
            </li>
          );
        })}
      </ol>
      <div className={`mt-5 rounded-2xl px-4 py-4 flex flex-wrap items-center gap-4 ${banner.tone === 'amber' ? 'bg-amber-50' : 'bg-primary/10'}`}>
        <span className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${banner.tone === 'amber' ? 'bg-amber-400 text-text' : 'bg-primary text-text'}`}><Icon name={banner.icon} size={22} /></span>
        <div className="flex-1 min-w-[220px]">
          <div className="font-bold text-text">{banner.title}</div>
          <div className="text-sm text-text/75 leading-relaxed">{banner.text}</div>
        </div>
        {(banner.actions || []).map(([label, target], i) => (
          <button key={target} type="button" onClick={() => onGo(target)} className={`${i === 0 ? 'bg-text text-white hover:opacity-90' : 'bg-white text-text hover:bg-gray-50 border border-gray-200'} rounded-xl px-4 py-2.5 text-sm font-semibold min-h-[44px]`}>{label}</button>
        ))}
      </div>
    </section>
  );
};

const Fact = ({ icon, label, value, good }) => (
  <div className="flex items-center gap-2.5 bg-background-auxiliary/70 rounded-xl px-3 py-2.5 min-w-0">
    <Icon name={icon} size={18} className="text-primary-dark shrink-0" />
    <div className="min-w-0"><div className="text-[11px] text-text/55">{label}</div><div className={`text-sm font-bold truncate ${good ? 'text-primary-dark' : 'text-text'}`}>{value}</div></div>
  </div>
);

// especificação depositada: o texto oficial é o português; o cliente abre a versão em inglês com um toque
const SpecBox = ({ c }) => {
  const [lang, setLang] = useState('pt');
  const pt = String(c.spec_pt || '').trim();
  const en = String(c.spec_en || '').trim();
  const mine = String(c.mine || '').trim();
  if (!pt) {
    if (!mine) return null;
    return (
      <div className="rounded-2xl bg-background-auxiliary/70 px-4 py-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-text/55 mb-1">Goods and services</div>
        <p className="text-sm text-text leading-relaxed">{mine}</p>
      </div>
    );
  }
  const tab = (k, t) => (
    <button type="button" onClick={() => setLang(k)} aria-pressed={lang === k} className={`text-xs font-semibold rounded-lg px-3 py-1.5 min-h-[32px] ${lang === k ? 'bg-text text-white' : 'text-text/70 hover:bg-white'}`}>{t}</button>
  );
  return (
    <div className="rounded-2xl bg-background-auxiliary/70 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-text/55">Goods and services as filed</div>
        <div className="inline-flex rounded-xl bg-background-auxiliary p-0.5" role="group" aria-label="Language">{tab('pt', 'Official text')}{tab('en', 'See in English')}</div>
      </div>
      {lang === 'pt' ? (
        <>
          <p lang="pt-BR" className="text-sm text-text leading-relaxed italic">{pt}</p>
          <p className="text-xs text-text/55 mt-2">The official text filed with INPI, in Portuguese.</p>
        </>
      ) : en ? (
        <>
          <p className="text-sm text-text leading-relaxed">{en}</p>
          <p className="text-xs text-text/55 mt-2">English version for your reference. The Portuguese text is the official one.</p>
        </>
      ) : (
        <>
          {mine && <p className="text-sm text-text leading-relaxed">{mine}</p>}
          <p className="text-xs text-text/55 mt-2">{mine ? 'This is the description you sent us. ' : ''}The English translation of the official text will be here within one business day.</p>
        </>
      )}
    </div>
  );
};

const BADGE_TONE = { wait: 'bg-amber-50 text-amber-800', alert: 'bg-red-50 text-red-700', done: 'bg-primary/10 text-primary-dark' };
const ClassCard = ({ c, conflicts }) => {
  const [bg, fg] = classTone(c.cls);
  const it = c.item;
  const opp = it && (it.deadlines || []).find((x) => x.key === 'opposition');
  const mine = (conflicts || []).filter((x) => String(x.new_classes || '').split(/[^0-9]+/).includes(String(c.cls)));
  const rank = it ? (RANK[it.stage] != null ? RANK[it.stage] : 0) : (c.pending ? -1 : 0);
  let pct = 0;
  if (opp && it.stage_since) { const total = Math.max(1, daysBetween(it.stage_since, opp.date)); pct = Math.min(100, Math.max(0, Math.round((daysBetween(it.stage_since, todayIso()) / total) * 100))); }
  const urgent = opp && opp.days <= 7;
  return (
    <section className="bg-background-white rounded-3xl border border-gray-100/80 shadow-sm p-5 flex flex-col gap-4" aria-label={`Class ${c.cls}`}>
      <div className="flex items-center gap-3.5">
        <span className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${bg} ${fg}`}><Icon name={classIcon(c.cls)} size={24} /></span>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-bold text-text leading-tight">{c.cls ? `Class ${c.cls}` : (c.mark || 'Your application')}</h3>
          <div className="text-xs text-text/60 leading-snug">{[CLASS_EN[Number(c.cls)], c.process ? `application ${c.process}` : 'not filed yet'].filter(Boolean).join(' · ')}</div>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1 whitespace-nowrap ${BADGE_TONE[it ? (STAGE_TONE[it.stage] || 'done') : 'wait']}`}>
          <Icon name={it ? (STAGE_ICON[it.stage] || 'clock') : c.pending ? 'form' : 'clock'} size={14} />{it ? it.stage_en : c.pending ? 'Preparing' : 'Waiting for publication'}
        </span>
      </div>
      {opp && opp.days >= 0 ? (
        <div className={`flex items-center gap-4 rounded-2xl px-4 py-3.5 ${urgent ? 'bg-amber-50' : 'bg-primary/10'}`}>
          <Icon name="hourglass" size={32} strokeWidth={1.8} className={urgent ? 'text-amber-800' : 'text-primary-dark'} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-2"><span className={`text-2xl font-extrabold ${urgent ? 'text-amber-900' : 'text-primary-dark'}`}>{opp.days === 0 ? 'Today' : `${opp.days} ${opp.days === 1 ? 'day' : 'days'}`}</span><span className={`text-sm ${urgent ? 'text-amber-900' : 'text-primary-dark'}`}>until {fmtLong(opp.date)}</span></div>
            <div className="text-xs text-text/60 mb-1.5">Third-party opposition period</div>
            <div className={`h-2 rounded-full overflow-hidden ${urgent ? 'bg-amber-100' : 'bg-primary/15'}`}><div className={`h-full rounded-full ${urgent ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${pct}%` }} /></div>
          </div>
        </div>
      ) : it && it.grant_date ? (
        <div className="flex items-center gap-4 rounded-2xl px-4 py-3.5 bg-primary/10"><Icon name="award" size={32} strokeWidth={1.8} className="text-primary-dark" /><div><div className="text-lg font-extrabold text-primary-dark">Registered</div><div className="text-sm text-text/70">on {fmtLong(it.grant_date)}, valid for 10 years</div></div></div>
      ) : c.pending ? (
        <div className="flex items-center gap-4 rounded-2xl px-4 py-3.5 bg-background-auxiliary/70"><Icon name="landmark" size={30} strokeWidth={1.8} className="text-text/60" /><div className="text-sm text-text/75">We file with INPI within 24 business hours after your details and signed Power of Attorney are in.</div></div>
      ) : null}
      {!c.pending && (
        <div className="grid grid-cols-2 gap-2.5">
          <Fact icon="landmark" label="Filed" value={c.filed_at ? SHORT(dmyIso(c.filed_at)) : 'Yes'} />
          <Fact icon="newspaper" label="Published" value={(it && it.pub_rpi) || c.rpi ? `${it && it.stage_since && rank >= 1 ? `${SHORT(it.stage_since)} · ` : ''}Gazette ${(it && it.pub_rpi) || c.rpi}` : 'Not yet'} />
          <Fact icon="eye" label="Similar marks" value={mine.length ? `${mine.length} found` : 'None so far'} good={!mine.length} />
          <Fact icon="search" label="Next" value={NEXT_EN[Math.max(0, Math.min(4, rank))]} />
        </div>
      )}
      {!c.pending && <SpecBox c={c} />}
    </section>
  );
};

const FILE_ICON = { protocolo: 'landmark', poa_signed: 'docSign', logo: 'image', certificado: 'award', nota_fiscal: 'receipt', rpi: 'newspaper' };
const DocRow = ({ icon, title, sub, href }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-gray-100 px-3 py-2.5">
    <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary-dark flex items-center justify-center shrink-0"><Icon name={icon} size={20} /></span>
    <div className="min-w-0 flex-1"><div className="text-sm font-bold text-text truncate">{title}</div><div className="text-xs text-text/55 truncate">{sub}</div></div>
    {href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" aria-label={`Download ${title}`} className="w-11 h-11 rounded-xl bg-text text-white flex items-center justify-center shrink-0 hover:opacity-90"><Icon name="download" size={20} /></a>
    ) : <span className="text-xs font-semibold text-primary-dark whitespace-nowrap flex items-center gap-1"><Icon name="check" size={14} />Received</span>}
  </div>
);

const Tile = ({ icon, tone, title, sub, children, dark }) => (
  <div className={`rounded-3xl p-5 flex flex-col gap-3 ${dark ? 'bg-text text-white' : 'bg-background-white border border-gray-100/80 shadow-sm'}`}>
    <div className="flex items-center gap-3">
      <span className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${tone}`}><Icon name={icon} size={28} /></span>
      <div><div className={`text-base font-bold ${dark ? 'text-white' : 'text-text'}`}>{title}</div><div className={`text-sm ${dark ? 'text-primary-lighter' : 'text-text/60'}`}>{sub}</div></div>
    </div>
    {children}
  </div>
);

const ClientPage = () => {
  const [token, setToken] = useState(readToken);
  const [reset, setReset] = useState(readReset);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const prev = document.title;
    document.title = 'Client area | TM RUSH';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = prev;
      document.head.removeChild(meta);
    };
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/tmr-client?t=${encodeURIComponent(token)}`);
      const j = await r.json();
      if (!j || !j.ok) throw new Error('We could not find this order. Please use the link in your order email, or sign in again.');
      setData(j);
      setError('');
      if (GO_TARGET) { const target = GO_TARGET; GO_TARGET = ''; setTimeout(() => { const el = document.getElementById(target); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 300); }
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again in a moment.');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  // as respostas de envio/salvar trazem só o pedido: mantém a vigilância e os serviços já carregados
  const merge = useCallback((v) => setData((d) => ({ ...(d || {}), ...(v || {}) })), []);

  const signOut = () => {
    try { localStorage.removeItem(KEY_STORE); } catch (e) { /* ignore */ }
    setToken('');
    setData(null);
  };

  if (!token || reset) {
    return <LoginView reset={reset} onToken={(t) => { try { localStorage.setItem(KEY_STORE, t); } catch (e) { /* storage blocked */ } setReset(''); setToken(t); }} />;
  }
  if (error) {
    return (
      <Shell onSignOut={signOut}>
        <div className="bg-background-white rounded-2xl p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-text mb-2">Your client area</h1>
          <p className="text-text/70 text-sm mb-4">{error}</p>
          <button type="button" onClick={signOut} className="text-sm font-semibold text-primary-dark hover:underline">Sign in with your email</button>
        </div>
      </Shell>
    );
  }
  if (!data) {
    return <Shell><div className="text-text/60 text-sm py-16 text-center">Loading your order…</div></Shell>;
  }

  const ck = data.checklist;
  const poaFiles = data.files.filter((x) => x.kind === 'poa_signed');
  const isWire = !!(data.payment && data.payment.method === 'transfer');
  const proofFiles = data.files.filter((x) => x.kind === 'payment_proof');
  const go = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const j = journeyOf(data);
  const watch = data.watch || { active: false, items: [], conflicts: [] };
  const watchOnly = isWatchOnly(data);
  let banner;
  if (data.editable && payPending(data)) banner = { tone: 'amber', icon: 'banknote', title: 'Action needed: upload your transfer receipt', text: data.next, actions: [['Upload the transfer receipt', 'payment'], ['Complete your details', 'details']] };
  else if (data.editable && !ck.details) banner = { tone: 'amber', icon: 'form', title: 'Action needed: complete your details', text: data.next, actions: [['Complete your details', 'details'], ['Power of Attorney', 'poa']] };
  else if (data.editable && !ck.poa) banner = { tone: 'amber', icon: 'docSign', title: 'Action needed: sign the Power of Attorney', text: data.next, actions: [['Sign the Power of Attorney', 'poa']] };
  else if ((data.offers || []).some((x) => !x.hired)) banner = { tone: 'amber', icon: 'alert', title: 'Your decision is needed', text: 'INPI published a decision on your application. See below what it means, the deadline and what we can do for you.', actions: [['See the options', 'services']] };
  else banner = { tone: 'green', icon: 'check', title: j.filed ? 'Nothing needed from you right now' : 'All set: we are preparing your filing', text: data.next };
  const byProc = new Map();
  const goodsOf = (cls) => { const g = (data.details && data.details.goods) || {}; return String(g[cls] || ((data.processes || []).length <= 1 ? g.all || '' : '') || ''); };
  (data.processes || []).forEach((p) => byProc.set(String(p.process), { process: String(p.process), cls: String(p.cls || ''), mark: p.mark, filed_at: p.filed_at, rpi: p.rpi, spec_pt: p.spec_pt || '', spec_en: p.spec_en || '', mine: goodsOf(String(p.cls || '')) }));
  (watch.items || []).forEach((i) => { const x = byProc.get(String(i.process)) || { process: String(i.process), cls: String(i.cls || ''), mark: data.marks[0] }; byProc.set(String(i.process), { ...x, item: i }); });
  let cards = [...byProc.values()].sort((a, b) => Number(a.cls) - Number(b.cls));
  if (!cards.length) cards = (data.class_list.length ? data.class_list : ['']).map((c) => ({ cls: c, pending: true, mark: data.marks[0] }));
  const allDl = (watch.items || []).flatMap((i) => i.deadlines || []);
  const useDl = allDl.find((x) => x.key === 'use');
  const renDl = allDl.find((x) => x.key === 'renewal');
  const watchDl = allDl.find((x) => x.key === 'watch');
  const grant = (watch.items || []).map((i) => i.grant_date).filter(Boolean).sort()[0] || '';
  const freeUntil = grant ? `${Number(grant.slice(0, 4)) + 1}${grant.slice(4, 10)}` : '';
  const actionNeeded = banner.tone === 'amber';
  const markName = data.marks.join(', ') || data.trademark;

  return (
    <Shell onSignOut={signOut}>
      {data.is_test && (
        <div className="bg-amber-50 text-amber-800 text-sm rounded-xl px-4 py-3 mb-5">Internal test order. Nothing is filed until the team confirms.</div>
      )}

      <section className="bg-text text-white rounded-3xl p-6 md:p-8 mb-6 shadow-md flex flex-wrap items-center gap-5">
        {data.logo_url
          ? <img src={data.logo_url} alt={`${markName} logo`} className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white object-contain p-1.5 shrink-0" />
          : <span className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/10 text-primary-lighter flex items-center justify-center shrink-0"><Icon name="tag" size={36} /></span>}
        <div className="min-w-0 flex-1 basis-72">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary-light mb-1.5">{data.kind} · Order {data.ref}</div>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight break-words">{markName}</h1>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-gray-300 mt-2">
            {data.classes && <span className="inline-flex items-center gap-1.5"><Icon name="tag" size={16} className="text-primary-light" />Class{data.class_list.length > 1 ? 'es' : ''} {data.classes}</span>}
            {data.owner && <span className="inline-flex items-center gap-1.5 min-w-0"><Icon name="users" size={16} className="text-primary-light shrink-0" /><span className="truncate">{data.owner}</span></span>}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {watch.active && <span className="inline-flex items-center gap-2 bg-primary/20 text-primary-lighter rounded-full px-3.5 py-1.5 text-sm font-semibold"><Icon name="eye" size={16} className="text-primary" />Brazil Watch active</span>}
          <span className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold ${actionNeeded ? 'bg-amber-400 text-text' : 'bg-white/10 text-white'}`}><Icon name={actionNeeded ? 'bell' : 'check'} size={16} />{actionNeeded ? 'Action needed from you' : 'No action needed from you'}</span>
        </div>
      </section>

      <Journey j={j} banner={banner} onGo={go} />

      {(data.offers || []).length > 0 && (
        <section id="services" className={card} aria-labelledby="services-title">
          <h2 id="services-title" className="text-lg font-bold text-text mb-1 flex items-center gap-2.5"><Icon name="alert" size={22} className="text-amber-700" />Your trademark needs a decision</h2>
          <p className="text-sm text-text/65 mb-4">INPI published something with a deadline. You decide whether to act; if you hire us, we take it from here.</p>
          <div className="space-y-3">{data.offers.map((x) => <ServiceOffer key={`${x.key}-${x.process}`} x={x} token={token} onChanged={load} />)}</div>
        </section>
      )}

      {data.editable && (
        <>
          <section className={card} aria-labelledby="ready-title">
            <h2 id="ready-title" className="text-lg font-bold text-text mb-1">Get your filing ready</h2>
            <p className="text-sm text-text/65 mb-5">{isWire ? 'Four steps on your side. As soon as everything is in, we file with INPI within 24 business hours.' : 'Three steps on your side. As soon as both are in, we file with INPI within 24 business hours.'}</p>
            <ol className={`grid grid-cols-1 gap-3 ${isWire ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
              {[...(isWire ? [['banknote', 'Upload the transfer receipt', 'The receipt is enough for us to file', !payPending(data), 'payment']] : []), ['form', 'Complete your details', 'Owner, trademark, goods and services', ck.details, 'details'], ['download', 'Download and sign', 'The Power of Attorney comes out with your details', ck.poa, 'poa'], ['upload', 'Upload the signed copy', 'PDF or photo, right here', ck.poa, 'poa']].map(([icon, t, s, ok, target], i) => (
                <li key={t}>
                  <button type="button" onClick={() => go(target)} className={`w-full text-left rounded-2xl p-4 flex items-start gap-3 min-h-[44px] ${ok ? 'bg-primary/10' : 'bg-background-auxiliary/70 hover:bg-background-auxiliary'}`}>
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${ok ? 'bg-primary text-text' : 'bg-white text-text border border-gray-200'}`}><Icon name={ok ? 'check' : icon} size={20} /></span>
                    <span className="min-w-0"><span className="block text-xs font-semibold text-text/50">Step {i + 1}</span><span className="block text-sm font-bold text-text">{t}</span><span className="block text-xs text-text/60">{ok ? 'Done' : s}</span></span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
          {isWire && (
            <section id="payment" className={card} aria-labelledby="payment-title">
              <h2 id="payment-title" className="text-lg font-bold text-text mb-1 flex items-center gap-2.5"><Icon name="banknote" size={22} className="text-primary-dark" />Bank transfer</h2>
              <p className="text-sm text-text/65 mb-4">{payPending(data) ? `Pay invoice ${data.payment.invoice} by international bank transfer. The bank details are on the invoice we emailed you. Then upload the bank receipt here (a SWIFT / MT103 copy or the confirmation from your bank). The receipt is enough for us to file: we do not wait for the funds to arrive.` : 'We have your transfer receipt. Thank you.'}</p>
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5 text-sm">
                <div className="rounded-xl bg-background-auxiliary/70 px-4 py-3"><dt className="text-xs font-semibold text-text/50">Invoice</dt><dd className="font-bold text-text">{data.payment.invoice}</dd></div>
                <div className="rounded-xl bg-background-auxiliary/70 px-4 py-3"><dt className="text-xs font-semibold text-text/50">Total</dt><dd className="font-bold text-text">{data.payment.total}</dd></div>
                <div className="rounded-xl bg-background-auxiliary/70 px-4 py-3"><dt className="text-xs font-semibold text-text/50">Payment reference</dt><dd className="font-bold text-text">{data.payment.invoice}</dd></div>
              </dl>
              {proofFiles.length > 0 && <div className="text-sm text-primary-dark mb-3 flex items-center gap-1.5"><Icon name="check" size={16} />Received {fmt(proofFiles[proofFiles.length - 1].at)}: {proofFiles[proofFiles.length - 1].name}</div>}
              <Uploader token={token} kind="payment_proof" label={proofFiles.length ? 'Upload another receipt' : 'Upload the transfer receipt'} accept="application/pdf,image/*" onDone={merge} />
              <div className="text-xs text-text/50 mt-2">PDF, JPG or PNG, up to {MAX_MB} MB. You can also reply to our email with it.</div>
            </section>
          )}
          <DetailsForm key={data.ref} view={data} token={token} onSaved={merge} />
          <section id="poa" className={card}>
            <h2 className="text-lg font-bold text-text mb-1 flex items-center gap-2.5"><Icon name="docSign" size={22} className="text-primary-dark" />Power of Attorney</h2>
            <p className="text-sm text-text/65 mb-5">Brazil requires a Power of Attorney so we can act for you before INPI. A simple signature is enough: no notarization, apostille or legalization. {ck.details ? 'Your document below already carries your details.' : 'Complete your details first and the document comes out with them, ready to sign.'}</p>
            <ol className="space-y-5">
              <li className="flex gap-4">
                <span className="w-10 h-10 rounded-full bg-primary/10 text-primary-dark flex items-center justify-center shrink-0"><Icon name="download" size={20} /></span>
                <div className="min-w-0">
                  <div className="font-semibold text-text mb-2">1. Download it</div>
                  <a href={data.poa_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-text text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 min-h-[44px]"><Icon name="download" size={18} />Power of Attorney (PDF)</a>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="w-10 h-10 rounded-full bg-primary/10 text-primary-dark flex items-center justify-center shrink-0"><Icon name="pencil" size={20} /></span>
                <div className="min-w-0"><div className="font-semibold text-text">2. Sign it</div><div className="text-sm text-text/60">Print and sign, or sign electronically. {data.details.owner_type === 'company' ? 'For a company, an authorized officer signs.' : ''}</div></div>
              </li>
              <li className="flex gap-4">
                <span className="w-10 h-10 rounded-full bg-primary/10 text-primary-dark flex items-center justify-center shrink-0"><Icon name="upload" size={20} /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-text mb-2">3. Upload the signed copy</div>
                  {poaFiles.length > 0 && <div className="text-sm text-primary-dark mb-3 flex items-center gap-1.5"><Icon name="check" size={16} />Received {fmt(poaFiles[poaFiles.length - 1].at)}: {poaFiles[poaFiles.length - 1].name}</div>}
                  <Uploader token={token} kind="poa_signed" label={poaFiles.length ? 'Upload a new copy' : 'Upload signed copy'} accept="application/pdf,image/*" onDone={merge} />
                  <div className="text-xs text-text/50 mt-2">PDF, JPG or PNG, up to {MAX_MB} MB. You can also reply to our email with it.</div>
                </div>
              </li>
            </ol>
          </section>
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {cards.map((c) => <ClassCard key={c.process || `c${c.cls}`} c={c} conflicts={watch.conflicts} />)}
      </div>

      {(j.filed || watch.active) && (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6" aria-label="Brazil Watch and future milestones">
          <Tile dark icon="eye" tone="bg-primary/20 text-primary" title="Brazil Watch" sub={watch.active ? 'On · every Tuesday' : 'Starts with your filing'}>
            {watch.active && <div className="flex items-baseline gap-2"><span className="text-4xl font-extrabold">{watch.conflicts.length}</span><span className="text-sm text-gray-300">similar {watch.conflicts.length === 1 ? 'mark' : 'marks'} found</span></div>}
            <div className="text-sm text-gray-300 leading-relaxed">
              {watch.last_rpi ? `Last Gazette checked: no. ${watch.last_rpi}. ` : ''}
              {watchOnly ? (watchDl ? `Active until ${fmtLong(watchDl.date)}.` : '') : freeUntil ? `Free until ${fmtLong(freeUntil)}, 12 months after registration.` : 'Free for the first 12 months after registration.'}
            </div>
          </Tile>
          <Tile icon="activity" tone="bg-sky-50 text-sky-800" title="Use requirement" sub={useDl ? fmtLong(useDl.date) : '5 years after registration'}>
            <div className="text-sm text-text/65 leading-relaxed">Keep using the mark in Brazil. {useDl ? 'We remind you ahead of time.' : 'The exact date shows here once INPI registers it, and we remind you ahead of time.'}</div>
          </Tile>
          <Tile icon="refresh" tone="bg-violet-50 text-violet-800" title="Renewal" sub={renDl ? fmtLong(renDl.date) : '10 years after registration'}>
            <div className="text-sm text-text/65 leading-relaxed">A registration lasts 10 years and can be renewed again and again. We track the date for you.</div>
          </Tile>
        </section>
      )}

      {watch.conflicts.length > 0 && (
        <section className={card} aria-labelledby="similar-title">
          <h2 id="similar-title" className="text-lg font-bold text-text mb-3 flex items-center gap-2.5"><Icon name="alert" size={22} className="text-amber-700" />Similar marks found by your watch</h2>
          <div className="space-y-2">
            {watch.conflicts.map((c) => (
              <div key={`${c.rpi}-${c.new_process}`} className="rounded-2xl bg-amber-50/70 px-4 py-3 text-sm text-text">
                <b>{c.new_mark}</b> · application {c.new_process} · class {c.new_classes} · Gazette {c.rpi}{c.opposition_deadline ? ` · opposition period until ${fmtLong(c.opposition_deadline)}` : ''}
              </div>
            ))}
          </div>
          <p className="text-xs text-text/55 mt-3">Our team reviews each one and writes to you with the facts. Whether to act is always your decision.</p>
        </section>
      )}

      {(data.services || []).length > 0 && (
        <section className={card}>
          <h2 className="text-lg font-bold text-text mb-4">Other services</h2>
          <div className="divide-y divide-gray-100">
            {data.services.map((x, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div>
                  <div className="font-semibold text-text">{x.title}</div>
                  <div className="text-xs text-text/60">Started {fmt(x.opened_at)}{x.petition ? ` · Petition ${x.petition}` : ''}</div>
                </div>
                <span className={`text-xs font-semibold rounded-full px-3 py-1 ${x.status === 'Filed with INPI' ? 'bg-primary/15 text-primary-dark' : 'bg-amber-50 text-amber-800'}`}>{x.status}{x.invoice && x.status === 'Waiting for payment' ? ` · invoice ${x.invoice}` : ''}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {!data.is_test && (
        <section className="bg-primary/10 border border-primary/25 rounded-3xl p-5 md:p-6 mb-6" aria-labelledby="more-title">
          <h2 id="more-title" className="text-lg font-bold text-text mb-4">Protect more in Brazil</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-background-white rounded-2xl p-4 flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary-dark flex items-center justify-center shrink-0"><Icon name="tagPlus" size={24} /></span>
              <div className="flex-1 min-w-0"><div className="font-bold text-text">Register another mark or class</div><div className="text-sm text-text/60">From USD 530, government fee included</div></div>
              <a href="/order" className="bg-text text-white rounded-xl px-4 py-2.5 text-sm font-semibold hover:opacity-90 min-h-[44px] flex items-center">Start</a>
            </div>
            <div className="bg-background-white rounded-2xl p-4 flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-800 flex items-center justify-center shrink-0"><Icon name="eye" size={24} /></span>
              <div className="flex-1 min-w-0"><div className="font-bold text-text">Watch a competitor&apos;s mark</div><div className="text-sm text-text/60">USD 150 per mark per year, reports in English</div></div>
              <a href="/brazil-watch" className="bg-text text-white rounded-xl px-4 py-2.5 text-sm font-semibold hover:opacity-90 min-h-[44px] flex items-center">Add</a>
            </div>
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-background-white rounded-3xl p-5 md:p-6 border border-gray-100/80 shadow-sm space-y-2.5">
          <h2 className="text-lg font-bold text-text mb-2 flex items-center gap-2.5"><Icon name="folder" size={22} className="text-primary-dark" />Documents</h2>
          {data.editable && <DocRow icon="docSign" title="Power of Attorney (to sign)" sub="PDF · ready with your details" href={data.poa_url} />}
          {data.files.map((x) => <DocRow key={x.id} icon={FILE_ICON[x.kind] || 'file'} title={x.label} sub={`${String(x.name || '').split('.').pop().toUpperCase()} · ${fmt(x.at)}`} href={x.url} />)}
          {!data.files.some((x) => x.kind === 'certificado') && (
            <div className="flex items-center gap-3 rounded-2xl border border-dashed border-gray-200 px-3 py-2.5 text-text/60">
              <span className="w-10 h-10 rounded-xl bg-background-auxiliary flex items-center justify-center shrink-0"><Icon name="award" size={20} /></span>
              <span className="text-sm">Your registration certificate and invoice appear here when they are ready.</span>
            </div>
          )}
        </div>
        <Messages view={data} token={token} onSent={merge} />
      </section>

      {data.timeline.length > 0 && (
        <details className="bg-background-white rounded-3xl p-5 md:p-6 border border-gray-100/80 shadow-sm group">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-3 min-h-[44px]">
            <span className="text-lg font-bold text-text flex items-center gap-2.5"><Icon name="clock" size={22} className="text-primary-dark" />Full history</span>
            <span className="text-sm font-semibold text-primary-dark">{data.timeline.length} events</span>
          </summary>
          <ol className="relative border-l-2 border-gray-100 ml-2 space-y-4 mt-5">
            {[...data.timeline].reverse().map((e, i) => (
              <li key={i} className="ml-5">
                <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${i === 0 ? 'bg-primary' : 'bg-gray-300'}`} aria-hidden="true" />
                <div className="text-xs text-text/50">{fmtLong(e.at)}</div>
                <div className="text-sm text-text leading-snug">{e.text}</div>
              </li>
            ))}
          </ol>
        </details>
      )}
    </Shell>
  );
};

export default ClientPage;
